import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';

// 创建认证上下文
const AuthContext = createContext();

// 自定义Hook，用于在组件中访问认证上下文
export function useAuth() {
  return useContext(AuthContext);
}

// 认证上下文提供者组件
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  
  // 从本地存储加载用户数据
  useEffect(() => {
    const storedUser = localStorage.getItem('currentUser');
    const token = localStorage.getItem('token');
    
    if (storedUser && token) {
      try {
        setCurrentUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Failed to parse stored user data', e);
        localStorage.removeItem('currentUser');
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  }, []);

  // 保存用户数据到本地存储
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem('currentUser', JSON.stringify(currentUser));
    } else {
      localStorage.removeItem('currentUser');
      localStorage.removeItem('token');
    }
  }, [currentUser]);
  
  // 登录处理函数
  const login = useCallback(async (email, password) => {
    setLoading(true);
    setError('');
    
    try {
      console.log('尝试登录:', email);
      
      // 使用authService进行API登录
      const response = await authService.login(email, password);
      console.log('登录响应:', response);
      
      // 设置用户数据
      setCurrentUser(response.user);
      
      // 存储JWT令牌
      localStorage.setItem('token', response.token);
      
      console.log('登录成功:', response.user);
      console.log('存储的令牌:', response.token);
      console.log('准备导航到仪表盘');
      navigate('/dashboard');
      return true;
    } catch (error) {
      console.error('登录错误详情:', error);
      setError(error.message || '登录失败，请检查您的凭据');
      return false;
    } finally {
      setLoading(false);
    }
  }, [navigate]);
  
  // 注册处理函数
  const register = useCallback(async (name, email, password) => {
    setLoading(true);
    setError('');
    
    try {
      console.log('尝试注册:', email);
      
      // 使用authService进行API注册
      const response = await authService.register(name, email, password);
      console.log('注册响应:', response);
      
      // 设置用户数据
      setCurrentUser(response.user);
      
      // 存储JWT令牌
      localStorage.setItem('token', response.token);
      
      console.log('注册成功:', response.user);
      console.log('存储的令牌:', response.token);
      console.log('准备导航到仪表盘');
      navigate('/dashboard');
      return true;
    } catch (error) {
      console.error('注册错误详情:', error);
      setError(error.message || '注册失败，请稍后再试');
      return false;
    } finally {
      setLoading(false);
    }
  }, [navigate]);
  
  // 登出处理函数
  const logout = useCallback(() => {
    console.log('用户登出');
    setCurrentUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('currentUser');
    navigate('/login');
  }, [navigate]);
  
  // 更新用户资料
  const updateProfile = async (userData) => {
    setError(null);
    try {
      console.log('更新用户资料:', userData);
      const updatedUser = await authService.updateProfile(userData);
      setCurrentUser({
        ...currentUser,
        ...updatedUser
      });
      return updatedUser;
    } catch (err) {
      const errorMsg = err.message || '更新资料失败';
      console.error(errorMsg, err);
      setError(errorMsg);
      throw new Error(errorMsg);
    }
  };

  // 更新密码
  const updatePassword = async (currentPassword, newPassword) => {
    setError(null);
    try {
      console.log('更新密码');
      const result = await authService.updatePassword(currentPassword, newPassword);
      return result;
    } catch (err) {
      const errorMsg = err.message || '更新密码失败';
      console.error(errorMsg, err);
      setError(errorMsg);
      throw new Error(errorMsg);
    }
  };

  // 上下文提供的值
  const value = {
    currentUser,
    loading,
    error,
    login,
    register,
    logout,
    setError,
    updateProfile,
    updatePassword
  };
  
  // 注意：总是渲染children，即使正在加载中
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider; 